import { BrowserCommandClient } from '@apmplus/web';
import { NioApmPlusAutoReportClickEventConfig } from '../model';

const AUTO_REPORT_CLICK_EVENT_PLUGIN_ATTRIBUTE_NAMES = {
    TYPE: 'ualt-action-type',
    EVENT_NAME: 'ualt-action-event-name',
    EVENT_METRICS: 'ualt-action-event-metrics',
    EVENT_CATEGORIES: 'ualt-action-event-categories',
    LOG_CONTENT: 'ualt-action-log-content',
    LOG_LEVEL: 'ualt-action-log-level',
    LOG_EXTRA: 'ualt-action-log-extra',
};

export function initAutoReportClickEventPlugin(
    client: BrowserCommandClient,
    clientType: 'standard' | 'minimal',
    options?: NioApmPlusAutoReportClickEventConfig
) {
    if (!client || !options?.enable) {
        return;
    }

    if (typeof window === 'undefined' || !window?.document || !window?.document?.addEventListener) {
        return;
    }

    const { TYPE, EVENT_NAME, EVENT_METRICS, EVENT_CATEGORIES, LOG_CONTENT, LOG_LEVEL, LOG_EXTRA } =
        AUTO_REPORT_CLICK_EVENT_PLUGIN_ATTRIBUTE_NAMES;

    window.document?.addEventListener?.(
        'click',
        (event: MouseEvent) => {
            try {
                if (!event) return;

                const target = (event?.target ?? event?.srcElement) as Node;

                let depth = options?.depth ?? 7;
                for (
                    let el: Node | null | undefined = target;
                    !!el && el?.nodeType !== 9 && depth >= 0;
                    el = el?.parentNode
                ) {
                    depth--;

                    if (el?.nodeType !== 1) continue;

                    // nodeType 为 1 时, el 为 Element 类型
                    const _el = el as Element;

                    const eventName = _el?.getAttribute?.(EVENT_NAME);
                    const logContent = _el?.getAttribute?.(LOG_CONTENT);
                    const type = _el?.getAttribute?.(TYPE) ?? options?.defaultType ?? TYPE;

                    if (type === 'log') {
                        // log 必须有 content
                        if (!logContent) continue;

                        const level = _el?.getAttribute?.(LOG_LEVEL) ?? options?.defaultLogLevel ?? 'info';
                        const extra = JSON.parse(_el?.getAttribute?.(LOG_EXTRA) ?? '{}');

                        if (clientType === 'standard') {
                            client?.sendLog?.({
                                level: level,
                                content: logContent,
                                extra: extra,
                            });
                        } else {
                            client?.report?.({
                                ev_type: 'custom',
                                payload: {
                                    level: level,
                                    content: logContent,
                                    categories: extra,
                                    type: 'log',
                                },
                            });
                        }
                        return;
                    } else if (type === 'event') {
                        // event 必须有 name
                        if (!eventName) continue;

                        const metrics = JSON.parse(_el?.getAttribute?.(EVENT_METRICS) ?? '{}');
                        const categories = JSON.parse(_el?.getAttribute?.(EVENT_CATEGORIES) ?? '{}');

                        if (clientType === 'standard') {
                            client?.sendEvent?.({
                                name: eventName,
                                metrics: metrics,
                                categories: categories,
                            });
                        } else {
                            client?.report?.({
                                ev_type: 'custom',
                                payload: {
                                    name: eventName,
                                    metrics: metrics,
                                    categories: categories,
                                    type: 'event',
                                },
                            });
                        }
                        return;
                    }
                }
            } catch (e) {
                console.log('[NIO APM Plus] autoReportClickEventPlugin failed', e);
            }
        },
        true
    );
}
